import React from "react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  SkeletonCard,
  skeletonRows,
} from "../../../components/ui";
import type { BrandService, ServiceStatus } from "../types/brand.types";

/** The same two pills the plans table wears, so one screen has one status. */
const STATUS_STYLES: Record<ServiceStatus, string> = {
  Active: "bg-brand-soft text-[#00899B]",
  "In-active": "bg-[#F1F4F6] text-ink-muted",
};

/**
 * How many blocks stand in while the list is on its way. Eight, because that
 * is roughly a screenful at the widths this grid is usually read at — enough
 * to look like a list arriving, few enough not to invent one.
 */
const SKELETON_CARDS = 8;

interface ServicesGridProps {
  services: BrandService[];
  /** Opens the card's details. The whole card is the control. */
  onSelect: (service: BrandService) => void;
  /** Stands in for the cards when the list comes back with none. */
  empty: EmptyStateProps;
  /**
   * Where the list is. Nothing is drawn while it is merely on its way, and
   * only a wait long enough to notice shimmers. Cards already in hand are left
   * alone either way — a grid with something to show goes on showing it while
   * the next answer is fetched.
   */
  phase?: LoadPhase;
  /**
   * Whether the grid goes to the edges of whatever holds it, with no gutters
   * of its own. For the phone, where the tab draws no card around the list
   * and the page's own padding is the only inset the cards want.
   */
  flush?: boolean;
}

/**
 * The services a brand offers, as cards rather than as rows.
 *
 * A service is a name and a sentence, which is two short fields — a table of
 * them would be one wide column and a lot of white space, and the sentence is
 * the part worth reading. The card puts it under the name and keeps the date
 * and the pill on one line at the foot, so every card is the same height
 * whatever its description runs to.
 *
 * This is the tab's only scroll region: the header above it and the card it
 * sits in stay put, and `min-h-0` is what lets it shrink inside that column
 * rather than push it open.
 */
const ServicesGrid: React.FC<ServicesGridProps> = ({
  services,
  empty,
  onSelect,
  phase = "ready",
  flush = false,
}) => {
  // The scroll region itself, with nothing inside it assumed. The card gutters
  // are separate because the empty state does not want them: it carries its own
  // inset — see `EmptyState`'s welcome layout — and the Plans tab draws the same
  // block in a bare `td`, so a second set of gutters here would set the one
  // state a card's padding further right than the identical one next door.
  const region = "flex-1 min-h-0 overflow-auto custom-scrollbar-overlay";
  const gutters = flush ? "pb-4" : "px-4 sm:px-6 pb-4 sm:pb-6";
  const grid =
    "grid gap-4 3xl:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

  if (phase !== "ready" && services.length === 0) {
    return (
      <div className={`${region} ${gutters}`}>
        <div className={grid}>
          {skeletonRows(SKELETON_CARDS).map((index) => (
            <SkeletonCard key={index} phase={phase} className="h-[132px]" />
          ))}
        </div>
      </div>
    );
  }

  if (services.length === 0) {
    // A column rather than a row, so the block is centred down the region the
    // way a table's `align-middle` cell centres one, and left the full width
    // across it. A row would shrink it to its content and centre that too,
    // which pulls a welcome state's left-set headline and bullets into the
    // middle of the card and reads nothing like the same state on the tabs
    // either side of this one.
    //
    // Without the gutters, for the reason `region` gives: the same state on
    // the Plans tab stands in a `td` with none, and the two are read one after
    // the other by somebody setting a workspace up.
    return (
      <div className={`${region} flex flex-col justify-center`}>
        <EmptyState {...empty} />
      </div>
    );
  }

  return (
    <div className={`${region} ${gutters}`}>
      <ul className={grid}>
        {services.map((service) => (
          <li key={service.id}>
            <ServiceCard service={service} onSelect={onSelect} />
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * One service, and the control that opens it.
 *
 * The whole card is the button rather than a link or a menu in its corner: a
 * service is a name and a sentence, and there is exactly one thing to do with
 * one — open it. A hit target that small in a corner would be the harder half
 * of the card to press on a phone, which is where this grid is one column
 * wide and the card is already the width of the screen.
 *
 * `h-full` with the footer pushed down by `mt-auto`: the grid stretches every
 * card in a row to the tallest of them, and without it a one-line description
 * would leave its date and pill floating halfway up the card beside a
 * two-line one.
 */
const ServiceCard: React.FC<{
  service: BrandService;
  onSelect: (service: BrandService) => void;
}> = ({ service, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(service)}
    // Named in full for a screen reader, which reads the button rather than
    // the card: the heading inside is not enough on its own to say what
    // pressing it does.
    aria-label={`View ${service.name}`}
    className="flex h-full w-full flex-col card-surface border border-line bg-white p-4 text-left transition-colors hover:border-brand focus:outline-none focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/40 3xl:p-5"
  >
    <h3 className="text-[15px] 3xl:text-[16px] font-medium text-ink">
      {service.name}
    </h3>

    {/* Two lines and then an ellipsis. A description is a line about what the
        service is, not a page — and a card that grew to fit one written as a
        page would set the height of every card in its row. */}
    {service.description && (
      <p className="mt-1.5 line-clamp-2 text-[13px] 3xl:text-[14px] leading-[1.6] text-ink-muted">
        {service.description}
      </p>
    )}

    <div className="mt-auto flex items-center justify-between gap-3 pt-6">
      <span className="text-[12px] 3xl:text-[13px] text-ink-muted">
        {service.createdOn}
      </span>
      <span
        className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${
          STATUS_STYLES[service.status]
        }`}
      >
        {service.status}
      </span>
    </div>
  </button>
);

export default ServicesGrid;
